import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { getTimeTable, getUploadedTimeTable } from "../../../../services/StudentPortal/StudentTimeTableService";
import { unwrap } from "../../utils/studentPortalUtils";
import { handleApiError } from "../../../../utils/errorHandler";

// Confirmed against the real backend (2026-10-22): a date with no timetable
// responds 404 with {"message":{"errors":{"time_table":"No Time Table
// Records Found."}}} — that's an expected, normal empty state (most dates
// won't have one), not a failure, so it should never pop an error toast.
const isNoRecordsFound = (error) => error?.response?.status === 404;

// getTimeTable's real populated shape isn't confirmed yet (every test so far
// hit "No Time Table Records Found"), so this stays defensive about where a
// list of periods might live.
const extractPeriods = (data) => {
  if (Array.isArray(data) && data.length) return data;
  if (data && Array.isArray(data.periods) && data.periods.length) return data.periods;
  if (data && Array.isArray(data.timetable) && data.timetable.length) return data.timetable;
  return null;
};

// Confirmed against the real backend (2026-10-22): student/upload-timetable
// returns a LIST of upload records (one per upload, not one per period) —
// the actual file lives in each record's `timetable_docs`, which is null
// when nothing has been uploaded for it. The array itself can be non-empty
// while every record's file is still null, so "is there a timetable" has to
// check timetable_docs specifically, not just whether the array has entries
// (treating the raw array as "periods" would render its metadata as a
// garbage table — id/program_id/organization/sessions/... as columns).
const extractUploadedFileUrl = (data) => {
  const records = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
  const withFile = records.find((record) => record?.timetable_docs);
  return withFile?.timetable_docs || null;
};

/**
 * Holds the selected date (defaults to today) plus either a list of
 * structured periods (from getTimeTable) or an uploaded file URL (from
 * getUploadedTimeTable, tried only if getTimeTable has no periods) — never
 * raw, unshaped response data for the page to guess at.
 */
const useStudentTimetable = (studentId, programId, sessionId, initialDate = new Date()) => {
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [periods, setPeriods] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTimetable = useCallback(async (date) => {
    if (!studentId || !programId || !sessionId) return;
    setIsLoading(true);
    try {
      // Confirmed against the real backend (2026-10-22): student_id,
      // program_id and session_id are all required alongside the date.
      const params = {
        student_id: studentId,
        program_id: programId,
        session_id: sessionId,
        date: format(date, "yyyy-MM-dd"),
      };

      let resolvedPeriods = null;
      try {
        const response = await getTimeTable(params);
        resolvedPeriods = extractPeriods(unwrap(response, null));
      } catch (error) {
        if (!isNoRecordsFound(error)) handleApiError(error);
      }

      let resolvedFileUrl = null;
      if (!resolvedPeriods) {
        try {
          const uploadedResponse = await getUploadedTimeTable(params);
          resolvedFileUrl = extractUploadedFileUrl(unwrap(uploadedResponse, null));
        } catch (error) {
          if (!isNoRecordsFound(error)) handleApiError(error);
        }
      }

      setPeriods(resolvedPeriods);
      setFileUrl(resolvedFileUrl);
    } finally {
      setIsLoading(false);
    }
  }, [studentId, programId, sessionId]);

  useEffect(() => {
    fetchTimetable(selectedDate);
  }, [selectedDate, fetchTimetable]);

  return { selectedDate, setSelectedDate, periods, fileUrl, isLoading };
};

export default useStudentTimetable;
